import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUpRight, Briefcase, Check, MapPin, Paperclip } from 'lucide-react';
import { COMPANY, FORMSUBMIT_URL, JOBS } from '../data/site';

const inputCls =
  'w-full bg-transparent border border-clay px-4 py-3.5 text-sm text-ink outline-none rounded-none placeholder:text-ink/35 focus:border-terra transition-colors duration-200';

export default function Careers() {
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', position: JOBS[0].title, message: '' });
  const [resume, setResume] = useState(null);
  const [status, setStatus] = useState('idle');

  const apply = (title) => {
    setForm((f) => ({ ...f, position: title }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const data = new FormData();
      data.append('_subject', `Job Application — ${form.position}`);
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (resume) data.append('attachment', resume);
      const res = await fetch(FORMSUBMIT_URL, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      setStatus(res.ok ? 'sent' : 'error');
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <main data-testid="careers-page" className="pt-40 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">CAREERS — GROW WITH PRAKRUTI</p>
        <motion.h1
          initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink mb-8"
          data-testid="careers-headline"
        >
          Build the future<br />with us
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
          className="text-base lg:text-lg text-ink/60 leading-relaxed max-w-2xl mb-20"
        >
          Layouts, construction, materials — and soon, solar. We hire people who take ownership,
          and we promote from the site up.
        </motion.p>

        <div className="border-t border-clay mb-24">
          {JOBS.map((job, i) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-center py-8 border-b border-clay"
              data-testid={`job-${job.id}`}
            >
              <div className="lg:col-span-1">
                <span className="w-11 h-11 bg-terra/10 text-terra flex items-center justify-center"><Briefcase size={17} /></span>
              </div>
              <div className="lg:col-span-4">
                <h3 className="font-heading font-bold text-xl tracking-tight text-ink">{job.title}</h3>
                <p className="mt-1.5 text-xs text-ink/50 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-terra font-medium tracking-[0.14em]">{job.type.toUpperCase()}</span>
                  <span className="flex items-center gap-1"><MapPin size={11} className="text-terra" /> {job.location}</span>
                </p>
              </div>
              <p className="lg:col-span-5 text-sm text-ink/60 leading-relaxed">{job.desc}</p>
              <div className="lg:col-span-2 lg:text-right">
                <button onClick={() => apply(job.title)} data-testid={`job-apply-${job.id}`}
                  className="text-[11px] tracking-[0.2em] font-medium text-ink border border-clay px-6 py-3 inline-flex items-center gap-2 hover:bg-moss hover:text-sand hover:border-moss transition-colors duration-300">
                  APPLY <ArrowDown size={13} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div ref={formRef} className="grid grid-cols-1 lg:grid-cols-12 gap-12 scroll-mt-28">
          <div className="lg:col-span-4">
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">APPLY ONLINE</p>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink leading-tight">
              Send your<br />application
            </h2>
            <p className="mt-5 text-sm text-ink/60 leading-relaxed">
              Attach your resume (PDF) and tell us briefly about yourself. Shortlisted candidates
              hear back within one week — or walk in to our office, {COMPANY.hours.toLowerCase()}.
            </p>
          </div>

          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="bg-stone border border-clay p-8 lg:p-12"
            >
              {status === 'sent' ? (
                <div className="py-16 text-center" data-testid="careers-success">
                  <span className="w-14 h-14 bg-moss text-sand flex items-center justify-center mx-auto mb-6"><Check size={24} /></span>
                  <h3 className="font-heading font-bold text-2xl tracking-tight text-ink mb-3">Application received</h3>
                  <p className="text-sm text-ink/60 max-w-sm mx-auto">Thank you for your interest in {COMPANY.name}. We will call you if your profile fits.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="careers-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">NAME</label>
                      <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="careers-input-name" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">PHONE</label>
                      <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="careers-input-phone" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">EMAIL</label>
                      <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="careers-input-email" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">POSITION</label>
                      <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="careers-select-position" className={inputCls}>
                        {JOBS.map((j) => <option key={j.id}>{j.title}</option>)}
                        <option>General Application</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">MESSAGE</label>
                    <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="careers-input-message" className={`${inputCls} resize-none`} placeholder="Experience, current role, notice period…" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">RESUME (PDF, MAX 5 MB)</label>
                    <label
                      data-testid="careers-resume-drop"
                      className="flex items-center gap-3 border border-dashed border-clay px-4 py-5 cursor-pointer text-sm text-ink/50 hover:border-terra hover:text-terra transition-colors duration-200"
                    >
                      <Paperclip size={15} />
                      {resume ? <span className="text-ink font-medium" data-testid="careers-resume-name">{resume.name}</span> : 'Click to attach your resume'}
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="careers-resume-input"
                        onChange={(e) => setResume(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  {status === 'error' && (
                    <p className="text-xs text-terra" data-testid="careers-error">Something went wrong. Please email your resume to {COMPANY.email} directly.</p>
                  )}
                  <button type="submit" disabled={status === 'sending'} data-testid="careers-submit-btn"
                    className="group bg-moss text-sand text-[11px] font-medium tracking-[0.22em] px-10 py-4 flex items-center gap-2 hover:bg-terra transition-colors duration-300 disabled:opacity-60">
                    {status === 'sending' ? 'SENDING…' : 'SUBMIT APPLICATION'}
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
}
